import {
  hasRangeScore,
  isScoringEligibleQuestion,
  hasTextLikeScore
} from '@/containers/RoutineDefinitionFormView/scoring/scoringShared'
import { toNullableNumber } from '@/containers/RoutineDefinitionFormView/utils'

import { ITEM_TYPES } from '../ChecklistCreation/constants'

const TEXT_LIKE_TYPES = new Set([
  ITEM_TYPES.TEXT,
  ITEM_TYPES.IMAGE,
  ITEM_TYPES.DATE,
  ITEM_TYPES.ACCOUNT,
])
const OPTION_TYPES = new Set([ITEM_TYPES.RADIOS, ITEM_TYPES.SELECT])
const RANGE_TYPES = new Set([ITEM_TYPES.NUMBER, ITEM_TYPES.SCALE])

function inferTextLikeMaxPoints(rule = {}) {
  if (!hasTextLikeScore({ scoring: { pointsIfAnswered: rule?.pointsIfAnswered } })) {
    return null
  }

  return toNullableNumber(rule?.pointsIfAnswered)
}

function inferSelectionMaxPoints(rule = {}) {
  const optionPoints = Array.isArray(rule?.optionPoints)
    ? rule.optionPoints
    : []

  if (!optionPoints.length) {
    return null
  }

  return optionPoints.reduce((maxPoints, option) => {
    const optionPointsValue = toNullableNumber(option?.points)
    return Math.max(maxPoints, optionPointsValue || 0)
  }, 0)
}

function inferRangeMaxPoints(rule = {}) {
  const withinRange = toNullableNumber(rule?.pointsIfWithinRange)
  const outOfRange = toNullableNumber(rule?.pointsIfOutOfRange)

  if (withinRange === null && outOfRange === null) {
    return null
  }

  if (
    !hasRangeScore({
      min: 0,
      max: 1,
      pointsIfWithinRange: withinRange,
    }) &&
    !hasRangeScore({
      min: 0,
      max: 1,
      pointsIfWithinRange: outOfRange,
    })
  ) {
    return null
  }

  return Math.max(withinRange || 0, outOfRange || 0)
}

function inferCheckboxMaxPoints(rule = {}) {
  return toNullableNumber(rule?.pointsIfAllSelected)
}

function getQuestionMaxPointsInferer(type) {
  if (TEXT_LIKE_TYPES.has(type)) {
    return inferTextLikeMaxPoints
  }

  if (OPTION_TYPES.has(type)) {
    return inferSelectionMaxPoints
  }

  if (RANGE_TYPES.has(type)) {
    return inferRangeMaxPoints
  }

  if (type === ITEM_TYPES.CHECKBOXES) {
    return inferCheckboxMaxPoints
  }

  return null
}

function inferQuestionMaxPoints(item = {}) {
  if (!isScoringEligibleQuestion(item)) {
    return null
  }

  const inferMaxPoints = getQuestionMaxPointsInferer(item?.type)
  const rule = item?.scoring?.rule

  if (!inferMaxPoints || !rule) {
    return null
  }

  return inferMaxPoints(rule)
}

function buildSectionScoreSummary(separator = {}) {
  const items = Array.isArray(separator?.items) ? separator.items : []
  const weightedScorePercentage = toNullableNumber(
    separator?.scoring?.weightedScorePercentage
  )
  const maxPoints = items.reduce((total, item) => {
    const itemMaxPoints = inferQuestionMaxPoints(item)
    return total + (itemMaxPoints || 0)
  }, 0)
  const points = toNullableNumber(separator?.scoring?.points)

  if (!maxPoints || points === null || weightedScorePercentage === null) {
    return null
  }

  return {
    points,
    maxPoints,
    weightedScorePercentage,
  }
}

function resolveRoutineScoreSource(routine = {}, checklistResponse = {}) {
  const routineTotalPoints = toNullableNumber(routine?.totalPoints)
  const routineScorePercentage = toNullableNumber(routine?.scorePercentage)

  if (routineTotalPoints !== null && routineScorePercentage !== null) {
    return {
      totalPoints: routineTotalPoints,
      scorePercentage: routineScorePercentage,
    }
  }

  const checklistRoutineScore = checklistResponse?.routine || {}
  const checklistTotalPoints = toNullableNumber(checklistRoutineScore?.totalPoints)
  const checklistScorePercentage = toNullableNumber(
    checklistRoutineScore?.scorePercentage
  )

  if (checklistTotalPoints !== null && checklistScorePercentage !== null) {
    return {
      totalPoints: checklistTotalPoints,
      scorePercentage: checklistScorePercentage,
    }
  }

  return null
}

function buildRoutineScoreSummary(routine = {}, checklistResponse = {}) {
  const routineScore = resolveRoutineScoreSource(routine, checklistResponse)

  if (!routineScore) {
    return null
  }

  return {
    totalPoints: routineScore.totalPoints,
    scorePercentage: routineScore.scorePercentage,
  }
}

export {
  buildRoutineScoreSummary,
  buildSectionScoreSummary,
  inferQuestionMaxPoints
}
